import { isAssignableTrack, type AssignableTrack } from "./types";

// Pure URL parsing for the funder workspace's review routes. Lives in the
// shared lib (not the client tree) because the HTML shell, client startup
// prefetches, mounted player, and server-side lead-video resolver must agree
// byte-for-byte on the tRPC input a URL implies. A second copy of these parsers
// would silently drift and turn an early fetch into a wasted duplicate request.

export type MeetingsInput = { roundId: string; track?: AssignableTrack };
export type ProposalClipsInput = {
  roundId: string;
  proposalId: string;
  includeMeetings: false;
};
export type RecommenderClipsInput = {
  roundId: string;
  recommenderId: string;
  includeMeetings: false;
};
export type WorkspaceVideoMediaInput =
  | ({ context: "meetings" } & MeetingsInput)
  | {
      context: "proposal";
      roundId: string;
      proposalId: string;
    }
  | {
      context: "recommender";
      roundId: string;
      recommenderId: string;
    };
export type VideoMediaInput = { videoId: string } | WorkspaceVideoMediaInput;

/**
 * Whether the query string names a video/meeting selection that overrides a
 * Review rail's pathname-derived lead. Presence is the contract, including an
 * empty value: the mounted players treat `?video=`, `?transcript=`, and
 * `?excerpt=` as targeted and decline the lead-media query in all three cases.
 */
export function workspaceMediaSearchTargeted(search: string): boolean {
  const params = new URLSearchParams(search);
  return (
    params.has("video") || params.has("transcript") || params.has("excerpt")
  );
}

// Entity ids are a prefix plus nanoid over [A-Za-z0-9_-]. Besides rejecting
// routes that the mounted workspace cannot resolve, the strict alphabet is a
// safety gate for values embedded in the shell's inline script: none of these
// characters can escape a JS string or close a <script> tag. A generated '-'
// can immediately follow the prefix, so rec--abc and p--abc are valid.
const PROPOSAL_ID_PATTERN = /^p-[A-Za-z0-9_-]+$/;
const RECOMMENDER_ID_PATTERN = /^rec-[A-Za-z0-9_-]+$/;

// Parse the path shape owned by useFunderWorkspaceRouteState. Its rail segment
// is optional, and the mounted workspace interprets an omitted rail as
// Overview; startup prefetching must make the same choice before that router
// mounts. Match the round path exactly so similarly prefixed routes (for
// example, /reviewer) cannot accidentally trigger funder-only work.
function workspaceReviewPathParts(
  roundId: string,
  pathname: string,
): string[] | null {
  const base = `/round/${roundId}/funder/review`;
  if (pathname === base || pathname === `${base}/`) return [];
  if (!pathname.startsWith(`${base}/`)) return null;
  return pathname.slice(base.length + 1).split("/");
}

/**
 * Whether a round URL can render a selected meeting or clip player. Unlike the
 * data-prefetch parsers below, this intentionally includes bare proposal and
 * recommender rails: their effective first selection is only known after the
 * round loads, but the shared player chunk can still warm immediately.
 */
export function workspaceMediaPathTargeted(
  roundId: string,
  pathname: string,
): boolean {
  const pathParts = workspaceReviewPathParts(roundId, pathname);
  if (pathParts == null) return false;
  const [rail = "overview"] = pathParts;
  return (
    rail === "overview" ||
    rail === "tracks" ||
    rail === "proposals" ||
    rail === "recommenders"
  );
}

// The getMeetings input the funder workspace will request for a given URL, or
// null when the URL doesn't land on a meetings surface. Mirrors OverviewPanel's
// query input exactly (Overview rail = every track, so no track filter; Tracks
// rail = the URL's track when it's a real assignable track):
// a mismatched input would prefetch into a cache entry nobody reads.
export function workspaceMeetingsInputForPath(
  roundId: string,
  pathname: string,
): MeetingsInput | null {
  const pathParts = workspaceReviewPathParts(roundId, pathname);
  if (pathParts == null) return null;
  const [rail = "overview", itemId] = pathParts;
  if (rail === "overview") return { roundId };
  if (rail === "tracks" && isAssignableTrack(itemId)) {
    return { roundId, track: itemId };
  }
  return null;
}

/** The exact clips input mounted by the funder workspace's proposal panel. */
export function funderProposalClipsInput(
  roundId: string,
  proposalId: string,
): ProposalClipsInput {
  return { roundId, proposalId, includeMeetings: false };
}

/**
 * The proposal-clips input encoded in a funder-workspace deep link. A bare
 * proposals rail has no URL-derived selection, so it cannot be fetched yet.
 */
export function workspaceProposalClipsInputForPath(
  roundId: string,
  pathname: string,
): ProposalClipsInput | null {
  const pathParts = workspaceReviewPathParts(roundId, pathname);
  if (pathParts == null) return null;
  const [rail, proposalId, extraSegment] = pathParts;
  if (
    rail !== "proposals" ||
    proposalId == null ||
    extraSegment != null ||
    !PROPOSAL_ID_PATTERN.test(proposalId)
  ) {
    return null;
  }
  return funderProposalClipsInput(roundId, proposalId);
}

/** The exact clips input mounted by the funder workspace's recommender panel. */
export function funderRecommenderClipsInput(
  roundId: string,
  recommenderId: string,
): RecommenderClipsInput {
  return { roundId, recommenderId, includeMeetings: false };
}

/**
 * The recommender-clips input encoded in a funder-workspace deep link. A bare
 * recommenders rail has no URL-derived selection, so it cannot be fetched yet.
 */
export function workspaceRecommenderClipsInputForPath(
  roundId: string,
  pathname: string,
): RecommenderClipsInput | null {
  const pathParts = workspaceReviewPathParts(roundId, pathname);
  if (pathParts == null) return null;
  const [rail, recommenderId, extraSegment] = pathParts;
  if (
    rail !== "recommenders" ||
    recommenderId == null ||
    extraSegment != null ||
    !RECOMMENDER_ID_PATTERN.test(recommenderId)
  ) {
    return null;
  }
  return funderRecommenderClipsInput(roundId, recommenderId);
}

/** The media query input for the video a meetings catalog initially selects. */
export function meetingsVideoMediaInput(
  input: MeetingsInput,
): WorkspaceVideoMediaInput {
  return { context: "meetings", ...input };
}

/** The media query input for the video a proposal clips strip initially selects. */
export function proposalVideoMediaInput(
  roundId: string,
  proposalId: string,
): WorkspaceVideoMediaInput {
  return { context: "proposal", roundId, proposalId };
}

/** The media query input for the video a recommender clips strip initially selects. */
export function recommenderVideoMediaInput(
  roundId: string,
  recommenderId: string,
): WorkspaceVideoMediaInput {
  return { context: "recommender", roundId, recommenderId };
}

/**
 * The signed-media input encoded in a funder Review pathname. The server
 * resolves the selected video inside the query, keeping that lookup off the
 * HTML response path while still letting the shell start the request before
 * app boot.
 */
export function workspaceVideoMediaInputForPath(
  roundId: string,
  pathname: string,
): WorkspaceVideoMediaInput | null {
  const meetingsInput = workspaceMeetingsInputForPath(roundId, pathname);
  if (meetingsInput != null) return meetingsVideoMediaInput(meetingsInput);

  const proposalInput = workspaceProposalClipsInputForPath(roundId, pathname);
  if (proposalInput != null) {
    return proposalVideoMediaInput(roundId, proposalInput.proposalId);
  }

  const recommenderInput = workspaceRecommenderClipsInputForPath(
    roundId,
    pathname,
  );
  if (recommenderInput != null) {
    return recommenderVideoMediaInput(roundId, recommenderInput.recommenderId);
  }

  return null;
}
